import React, { useEffect, useState } from 'react';
import {
  Box,
  Text,
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Stack,
  Textarea,
  useDisclosure,
} from '@chakra-ui/core';
import { PrimaryButton, SecondaryButton } from '../../../../common/components/Buttons';

const hasValues = value => value && Object.keys(value).length > 0;

const selectedResult = (reviewedResult, originalResult) =>
  hasValues(reviewedResult) ? reviewedResult : originalResult || {};

const linesToText = value => (Array.isArray(value) ? value.join('\n') : '');

const formatDateTime = value => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const time = date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  return `${date.toLocaleDateString('fr-FR')} à ${time}`;
};

const showAnalysisDebug = () => process.env.REACT_APP_SHOW_AI_ANALYSIS_DEBUG === 'true';

const administrativeDetailsText = value =>
  value
    .toString()
    .replace(
      'Informations administratives manquantes',
      'Informations administratives absentes ou différentes du dossier',
    );

const textToLines = value =>
  value
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean);

const booleanValue = value => {
  if (value === true) return 'true';
  if (value === false) return 'false';
  return 'unknown';
};

const decisionFromResultValues = (validityIsValid, conformityIsConformant, priceIsConformant) => {
  const resultValues = [validityIsValid, conformityIsConformant, priceIsConformant];

  if (resultValues.includes('false')) return 'non_conformant';
  if (resultValues.includes('unknown')) return 'uncertain';
  return 'conformant';
};

const decisionFromResults = (decision, validityIsValid, conformityIsConformant, priceIsConformant) => {
  const resultDecision = decisionFromResultValues(
    validityIsValid,
    conformityIsConformant,
    priceIsConformant,
  );

  if (decision === 'conformant' && resultDecision !== 'conformant') return resultDecision;

  return decision;
};

const RESULT_REVIEW_KEYS = [
  'validityIsValid',
  'conformityIsConformant',
  'priceIsConformant',
];

const clearResolvedIssues = (review, key, value) => {
  if (value !== 'true') return review;

  if (key === 'validityIsValid') {
    return {
      ...review,
      validityMissingFields: '',
      validityDetails: 'Informations administratives validées après contrôle manuel',
    };
  }

  if (key === 'conformityIsConformant') {
    return {
      ...review,
      conformityMissingItems: '',
      conformityExtraItems: '',
      conformityQuantityIssues: '',
      conformityDetails: 'Conformité validée après contrôle manuel',
    };
  }

  if (key === 'priceIsConformant') {
    return { ...review, priceFlags: '' };
  }

  return review;
};

const buildReview = analysis => {
  const validityResult = selectedResult(analysis?.reviewedValidityResult, analysis?.validityResult);
  const conformityResult = selectedResult(analysis?.reviewedConformityResult, analysis?.conformityResult);
  const priceResult = selectedResult(analysis?.reviewedPriceResult, analysis?.priceResult);
  const hasPriceConformity = Object.prototype.hasOwnProperty.call(priceResult, 'isConformant');
  const priceFlags = Array.isArray(priceResult.flags) ? priceResult.flags : [];
  const validityIsValid = booleanValue(validityResult.isValid);
  const conformityIsConformant = booleanValue(conformityResult.isConformant);
  const fallbackPriceConformity = priceFlags.length > 0 ? 'false' : 'unknown';
  const priceIsConformant = hasPriceConformity ? booleanValue(priceResult.isConformant) : fallbackPriceConformity;
  const finalDecision = decisionFromResults(
    analysis?.finalDecision || analysis?.effectiveConclusion || analysis?.conclusion || 'uncertain',
    validityIsValid,
    conformityIsConformant,
    priceIsConformant,
  );

  return {
    finalDecision,
    validityIsValid,
    validityMissingFields: linesToText(validityResult.missingFields),
    validityDetails: administrativeDetailsText(validityResult.details || ''),
    conformityIsConformant,
    conformityMissingItems: linesToText(conformityResult.missingItems),
    conformityExtraItems: linesToText(conformityResult.extraItems),
    conformityQuantityIssues: linesToText(conformityResult.quantityIssues),
    conformityDetails: conformityResult.details || '',
    priceIsConformant,
    priceFlags: linesToText(priceResult.flags),
    priceDetails: priceResult.details || '',
    reviewJustification: analysis?.reviewJustification || '',
  };
};

const resultBoolean = value => {
  if (value === 'true') return true;
  if (value === 'false') return false;
  return null;
};

const buildPayload = review => ({
  finalDecision: decisionFromResults(
    review.finalDecision,
    review.validityIsValid,
    review.conformityIsConformant,
    review.priceIsConformant,
  ),
  reviewedValidityResult: {
    isValid: resultBoolean(review.validityIsValid),
    missingFields: textToLines(review.validityMissingFields),
    details: review.validityDetails,
  },
  reviewedConformityResult: {
    isConformant: resultBoolean(review.conformityIsConformant),
    missingItems: textToLines(review.conformityMissingItems),
    extraItems: textToLines(review.conformityExtraItems),
    quantityIssues: textToLines(review.conformityQuantityIssues),
    details: review.conformityDetails,
  },
  reviewedPriceResult: {
    isConformant: resultBoolean(review.priceIsConformant),
    flags: textToLines(review.priceFlags),
    details: review.priceDetails,
  },
  reviewJustification: review.reviewJustification,
});

const decisionLabel = decision => {
  if (decision === 'conformant') return 'Conforme';
  if (decision === 'non_conformant') return 'Non conforme';
  return 'À vérifier';
};

const decisionStyle = decision => {
  if (decision === 'conformant') return { bg: '#E6F4EA', color: '#236B3C' };
  if (decision === 'non_conformant') return { bg: 'rgba(249, 97, 97, 0.16)', color: '#B33131' };
  return { bg: '#FEF3C7', color: '#92400E' };
};

const STATUS_TONE = {
  ok: { dot: '#2F855A', text: '#2F6F46', activeBg: '#2F855A' },
  warn: { dot: '#B7791F', text: '#8A5A0F', activeBg: '#B7791F' },
  alert: { dot: '#F96161', text: '#B33131', activeBg: '#F96161' },
  neutral: { dot: '#A0AEC0', text: '#4A5568', activeBg: '#23282D' },
};

const StatusDot = ({ tone }) => (
  <Box
    as="span"
    display="inline-block"
    w="8px"
    h="8px"
    rounded="full"
    bg={STATUS_TONE[tone].dot}
    flexShrink={0}
  />
);

const EditableText = ({ value, onChange, disabled, placeholder, minH = '76px' }) => (
  <Textarea
    value={value}
    onChange={event => onChange(event.target.value)}
    disabled={disabled}
    minH={minH}
    fontSize="sm"
    bg="white"
    borderColor="lightGray3"
    _focus={{ outline: 0, borderColor: 'coral.900' }}
    placeholder={placeholder}
  />
);

const SegmentedControl = ({ value, onChange, options, disabled, size = 'md' }) => {
  const py = size === 'lg' ? 3 : 2;
  const fontSize = size === 'lg' ? 'sm' : 'xs';
  return (
    <Box
      display="inline-flex"
      border="1px solid"
      borderColor="lightGray3"
      rounded="md"
      bg="white"
      p="3px"
      width={size === 'lg' ? '100%' : 'auto'}
    >
      {options.map(opt => {
        const isActive = String(value) === String(opt.value);
        const tone = opt.tone || 'neutral';
        return (
          <Box
            as="button"
            type="button"
            key={opt.value}
            onClick={() => !disabled && onChange(opt.value)}
            disabled={disabled}
            flex={size === 'lg' ? '1' : '0 0 auto'}
            px={size === 'lg' ? 4 : 3}
            py={py}
            fontSize={fontSize}
            fontWeight="bold"
            rounded="full"
            bg={isActive ? STATUS_TONE[tone].activeBg : 'transparent'}
            color={isActive ? 'white' : 'gray.600'}
            border="none"
            cursor={disabled ? 'not-allowed' : 'pointer'}
            opacity={disabled && !isActive ? 0.5 : 1}
            transition="background 0.15s, color 0.15s"
            _hover={!disabled && !isActive ? { bg: '#F1F3F5', color: '#23282D' } : {}}
            _focus={{ outline: 'none', boxShadow: 'none' }}
            textTransform="uppercase"
            letterSpacing="0.04em"
          >
            {opt.label}
          </Box>
        );
      })}
    </Box>
  );
};

const FieldLabel = ({ children }) => (
  <Text fontSize="xs" fontWeight="bold" color="gray.500" mb={2} lineHeight="1.35">
    {children}
  </Text>
);

const SectionCard = ({ title, tone = 'neutral', headerRight, children }) => (
  <Box bg="white" border="1px solid" borderColor="lightGray3" rounded="md" overflow="hidden">
    <Box
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      px={4}
      py={3}
      bg="#FAFBFC"
      borderBottom="1px solid"
      borderColor="lightGray3"
    >
      <Box display="flex" alignItems="center">
        <StatusDot tone={tone} />
        <Text fontSize="sm" fontWeight="bold" color="darkGray" ml={2}>
          {title}
        </Text>
      </Box>
      {headerRight}
    </Box>
    <Box p={4}>{children}</Box>
  </Box>
);

const SummaryLine = ({ label, tone, value }) => (
  <Box display="flex" alignItems="flex-start" justifyContent="space-between">
    <Box display="flex" alignItems="center" mr={4} minW={0}>
      <StatusDot tone={tone} />
      <Text fontSize="sm" color="darkGray" ml={2}>
        {label}
      </Text>
    </Box>
    <Text
      fontSize="sm"
      fontWeight="semibold"
      color={STATUS_TONE[tone].text}
      textAlign="right"
      flexShrink={0}
    >
      {value}
    </Text>
  </Box>
);

const issueSummary = (items, singular, plural) => {
  if (items.length === 0) return 'Aucun point détecté';
  return `${items.length} ${items.length > 1 ? plural : singular}`;
};

const priceSummary = (isConformant, priceFlags) => {
  if (isConformant === null) return 'Indéterminé';
  if (isConformant) return 'Conforme';
  if (priceFlags.length === 0) return 'Non conforme';
  return issueSummary(priceFlags, 'alerte', 'alertes');
};

const validitySummary = (isValid, missingFields) => {
  if (isValid === null) return 'Indéterminée';
  if (isValid) return 'Valide';
  return issueSummary(missingFields, 'champ à vérifier', 'champs à vérifier');
};

const conformitySummary = (isConformant, issues) => {
  if (isConformant === null) return 'Indéterminée';
  if (isConformant) return 'Conforme';
  return issueSummary(issues, 'point à vérifier', 'points à vérifier');
};

const validityTone = (isValid, missingFields) => {
  if (isValid === null) return 'warn';
  if (isValid) return 'ok';
  return missingFields.length > 0 ? 'alert' : 'warn';
};

const conformityTone = (isConformant, totalIssues) => {
  if (isConformant === null) return 'warn';
  if (isConformant) return 'ok';
  return totalIssues > 0 ? 'alert' : 'warn';
};

const priceTone = isConformant => {
  if (isConformant === null) return 'warn';
  return isConformant ? 'ok' : 'alert';
};

const appendModificationSection = (sections, title, items) => {
  const lines = Array.isArray(items)
    ? items.map(line => line.trim()).filter(Boolean)
    : textToLines(items || '');
  if (lines.length === 0) return;

  sections.push(`${title}\n${lines.map(line => `- ${line}`).join('\n')}`);
};

const buildModificationRequestMessage = review => {
  const sections = [];

  appendModificationSection(
    sections,
    'Validité administrative',
    review.validityMissingFields,
  );
  appendModificationSection(
    sections,
    'Préconisations - éléments manquants',
    review.conformityMissingItems,
  );
  appendModificationSection(
    sections,
    'Préconisations - éléments en trop',
    review.conformityExtraItems,
  );
  appendModificationSection(
    sections,
    'Préconisations - quantités ou dimensions',
    review.conformityQuantityIssues,
  );
  appendModificationSection(
    sections,
    'Prix',
    review.priceFlags,
  );

  if (sections.length > 0) return sections.join('\n\n');

  return [
    review.validityDetails,
    review.conformityDetails,
    review.priceDetails,
  ].map(value => (value || '').trim()).filter(Boolean).join('\n\n');
};

const analysisDebugValue = review => JSON.stringify({
  decision: review.finalDecision,
  validiteAdministrative: {
    valide: resultBoolean(review.validityIsValid),
    champs: textToLines(review.validityMissingFields),
    details: review.validityDetails,
  },
  preconisations: {
    conforme: resultBoolean(review.conformityIsConformant),
    elementsManquants: textToLines(review.conformityMissingItems),
    elementsEnTrop: textToLines(review.conformityExtraItems),
    quantitesOuDimensions: textToLines(review.conformityQuantityIssues),
    details: review.conformityDetails,
  },
  prix: {
    conforme: resultBoolean(review.priceIsConformant),
    alertes: textToLines(review.priceFlags),
    details: review.priceDetails,
  },
}, null, 2);

const FieldGrid = ({ columns, children }) => (
  <Box
    display="grid"
    gridTemplateColumns={{ base: '1fr', md: columns }}
    style={{ gap: '16px' }}
  >
    {children}
  </Box>
);

const AnalysisReviewFields = ({ review, updateReview, isReadOnly, requiresJustification }) => {
  const validityMissing = textToLines(review.validityMissingFields);
  const conformityMissing = textToLines(review.conformityMissingItems);
  const conformityExtra = textToLines(review.conformityExtraItems);
  const conformityQuantity = textToLines(review.conformityQuantityIssues);

  const validityCurrentValue = resultBoolean(review.validityIsValid);
  const conformityCurrentValue = resultBoolean(review.conformityIsConformant);
  const priceCurrentValue = resultBoolean(review.priceIsConformant);
  const validityCurrentTone = validityTone(validityCurrentValue, validityMissing);
  const conformityCurrentTone = conformityTone(
    conformityCurrentValue,
    conformityMissing.length + conformityExtra.length + conformityQuantity.length,
  );
  const priceCurrentTone = priceTone(priceCurrentValue);

  return (
    <Stack spacing={6}>
      <Box bg="white" border="1px solid" borderColor="lightGray3" rounded="md" p={4}>
        <FieldLabel>Décision finale</FieldLabel>
        <SegmentedControl
          size="lg"
          value={review.finalDecision}
          disabled={isReadOnly}
          onChange={value => updateReview('finalDecision', value)}
          options={[
            { value: 'conformant', label: 'Conforme', tone: 'ok' },
            { value: 'non_conformant', label: 'Non conforme', tone: 'alert' },
            { value: 'uncertain', label: 'À vérifier', tone: 'warn' },
          ]}
        />
        {requiresJustification && (
          <Box mt={4}>
            <FieldLabel>Motif de correction</FieldLabel>
            <EditableText
              value={review.reviewJustification}
              onChange={value => updateReview('reviewJustification', value)}
              disabled={isReadOnly}
              placeholder="Expliquez pourquoi la conclusion IA est corrigée"
              minH="88px"
            />
            {review.reviewJustification.trim().length === 0 && (
              <Text mt={2} fontSize="xs" color="red.500" fontWeight="bold">
                Motif obligatoire pour corriger la conclusion IA.
              </Text>
            )}
          </Box>
        )}
      </Box>

      <SectionCard
        title="Validité administrative"
        tone={validityCurrentTone}
        headerRight={(
          <SegmentedControl
            value={review.validityIsValid}
            disabled={isReadOnly}
            onChange={value => updateReview('validityIsValid', value)}
            options={[
              { value: 'true', label: 'Valide', tone: 'ok' },
              { value: 'false', label: 'Incomplète', tone: 'alert' },
              { value: 'unknown', label: 'Indéterminé', tone: 'warn' },
            ]}
          />
        )}
      >
        <FieldGrid columns="1fr 1fr">
          <Box>
            <FieldLabel>Champs à vérifier</FieldLabel>
            <EditableText
              value={review.validityMissingFields}
              onChange={value => updateReview('validityMissingFields', value)}
              disabled={isReadOnly}
              placeholder="Un champ à vérifier par ligne"
              minH="120px"
            />
          </Box>
          <Box>
            <FieldLabel>Détails administratifs</FieldLabel>
            <EditableText
              value={review.validityDetails}
              onChange={value => updateReview('validityDetails', value)}
              disabled={isReadOnly}
              placeholder="Notes administratives"
              minH="120px"
            />
          </Box>
        </FieldGrid>
      </SectionCard>

      <SectionCard
        title="Conformité aux préconisations"
        tone={conformityCurrentTone}
        headerRight={(
          <SegmentedControl
            value={review.conformityIsConformant}
            disabled={isReadOnly}
            onChange={value => updateReview('conformityIsConformant', value)}
            options={[
              { value: 'true', label: 'Conforme', tone: 'ok' },
              { value: 'false', label: 'Non conforme', tone: 'alert' },
              { value: 'unknown', label: 'Indéterminé', tone: 'warn' },
            ]}
          />
        )}
      >
        <Stack spacing={4}>
          <FieldGrid columns="1fr 1fr 1fr">
            <Box>
              <FieldLabel>Éléments manquants</FieldLabel>
              <EditableText
                value={review.conformityMissingItems}
                onChange={value => updateReview('conformityMissingItems', value)}
                disabled={isReadOnly}
                placeholder="Aucun élément manquant détecté"
                minH="110px"
              />
            </Box>
            <Box>
              <FieldLabel>Éléments en trop</FieldLabel>
              <EditableText
                value={review.conformityExtraItems}
                onChange={value => updateReview('conformityExtraItems', value)}
                disabled={isReadOnly}
                placeholder="Aucun élément en trop détecté"
                minH="110px"
              />
            </Box>
            <Box>
              <FieldLabel>Quantités ou dimensions</FieldLabel>
              <EditableText
                value={review.conformityQuantityIssues}
                onChange={value => updateReview('conformityQuantityIssues', value)}
                disabled={isReadOnly}
                placeholder="Aucun problème de quantité ou dimension détecté"
                minH="110px"
              />
            </Box>
          </FieldGrid>
          <Box mt={3}>
            <FieldLabel>Détails de conformité</FieldLabel>
            <EditableText
              value={review.conformityDetails}
              onChange={value => updateReview('conformityDetails', value)}
              disabled={isReadOnly}
              placeholder="Détails de conformité"
              minH="80px"
            />
          </Box>
        </Stack>
      </SectionCard>

      <SectionCard
        title="Prix"
        tone={priceCurrentTone}
        headerRight={(
          <SegmentedControl
            value={review.priceIsConformant}
            disabled={isReadOnly}
            onChange={value => updateReview('priceIsConformant', value)}
            options={[
              { value: 'true', label: 'Conforme', tone: 'ok' },
              { value: 'false', label: 'Non conforme', tone: 'alert' },
              { value: 'unknown', label: 'Indéterminé', tone: 'warn' },
            ]}
          />
        )}
      >
        <FieldGrid columns="1fr 1fr">
          <Box>
            <FieldLabel>Alertes prix</FieldLabel>
            <EditableText
              value={review.priceFlags}
              onChange={value => updateReview('priceFlags', value)}
              disabled={isReadOnly}
              placeholder="Alertes prix (une par ligne)"
              minH="110px"
            />
          </Box>
          <Box>
            <FieldLabel>Détails prix</FieldLabel>
            <EditableText
              value={review.priceDetails}
              onChange={value => updateReview('priceDetails', value)}
              disabled={isReadOnly}
              placeholder="Notes sur les prix"
              minH="110px"
            />
          </Box>
        </FieldGrid>
      </SectionCard>

      {showAnalysisDebug() && (
        <SectionCard title="Résultat complet de l'analyse" tone="neutral">
          <Textarea
            value={analysisDebugValue(review)}
            readOnly
            minH="220px"
            fontSize="sm"
            fontFamily="mono"
            bg="white"
            borderColor="lightGray3"
            _focus={{ outline: 0, borderColor: 'lightGray3' }}
          />
        </SectionCard>
      )}
    </Stack>
  );
};

export const AiAnalysisPanel = ({
  analysis,
  onLaunchOrRetry,
  onConfirm,
  onReviewSave,
  onSendModificationRequest,
  isConfirmed = false,
  isFinalized = false,
  isAutoFollowupDisabled = false,
}) => {
  const [review, setReview] = useState(() => buildReview(analysis));
  const { isOpen, onOpen, onClose } = useDisclosure();

  useEffect(() => {
    setReview(buildReview(analysis));
  }, [analysis?.id, analysis?.reviewedAt, analysis?.analyzedAt]);

  if (!analysis || analysis.error) {
    return (
      <Box mt={4}>
        <Text color="lightGray2">Analyse intelligente non disponible</Text>
      </Box>
    );
  }

  if (analysis.notFound) {
    const qualityControlRequired =
      analysis.automaticAnalysis?.status === 'quality_control_required';

    return (
      <Box mt={4}>
        <Text color="lightGray2" mb={2}>
          {qualityControlRequired
            ? 'Analyse automatique en attente'
            : 'Analyse intelligente non lancée pour ce devis'}
        </Text>
        {qualityControlRequired && (
          <Text color="lightGray2" mb={2}>
            Le dernier rapport doit être contrôlé avant le lancement automatique.
          </Text>
        )}
        <SecondaryButton size="sm" onClick={onLaunchOrRetry}>
          {qualityControlRequired ? 'Lancer manuellement' : "Lancer l'analyse"}
        </SecondaryButton>
      </Box>
    );
  }

  if (analysis.pollingTimedOut) {
    return (
      <Box mt={4}>
        <Text color="lightGray2">
          Analyse intelligente toujours en cours, réessayez plus tard
        </Text>
      </Box>
    );
  }

  if ((analysis.loading && !analysis.status) || analysis.status === 'pending' || analysis.status === 'processing') {
    return (
      <Box mt={4} display="flex" alignItems="center">
        <Spinner size="sm" mr={2} />
        <Text color="lightGray2">Analyse intelligente en cours</Text>
      </Box>
    );
  }

  if (analysis.status === 'failed') {
    return (
      <Box mt={4}>
        <Text color="red.500" mb={2}>
          Échec de l&apos;analyse intelligente
        </Text>
        {analysis.failureReason && (
          <Text color="lightGray2" mb={2}>
            Motif : {analysis.failureReason}
          </Text>
        )}
        <Button size="sm" onClick={onLaunchOrRetry} variantColor="red" variant="outline">
          Relancer l&apos;analyse
        </Button>
      </Box>
    );
  }

  if (analysis.status !== 'completed') {
    return (
      <Box mt={4}>
        <Text color="lightGray2">Analyse intelligente non disponible</Text>
      </Box>
    );
  }

  const isActionHidden = isConfirmed || isFinalized;
  const isReadOnly = isActionHidden || analysis.loading;
  const missingItems = textToLines(review.conformityMissingItems);
  const extraItems = textToLines(review.conformityExtraItems);
  const quantityIssues = textToLines(review.conformityQuantityIssues);
  const missingFields = textToLines(review.validityMissingFields);
  const priceFlags = textToLines(review.priceFlags);
  const totalConformityIssues = missingItems.length + extraItems.length + quantityIssues.length;
  const validitySummaryValue = resultBoolean(review.validityIsValid);
  const conformitySummaryValue = resultBoolean(review.conformityIsConformant);
  const priceSummaryValue = resultBoolean(review.priceIsConformant);

  const validitySummaryTone = validityTone(validitySummaryValue, missingFields);
  const conformitySummaryTone = conformityTone(conformitySummaryValue, totalConformityIssues);
  const priceSummaryTone = priceTone(priceSummaryValue);
  const validitySummaryText = validitySummary(validitySummaryValue, missingFields);
  const conformitySummaryText = conformitySummary(
    conformitySummaryValue,
    [...missingItems, ...extraItems, ...quantityIssues],
  );
  const finalDecision = decisionFromResults(
    review.finalDecision,
    review.validityIsValid,
    review.conformityIsConformant,
    review.priceIsConformant,
  );
  const isConformant = finalDecision === 'conformant';
  const isNonConformant = finalDecision === 'non_conformant';
  const requiresJustification = finalDecision !== analysis.conclusion;
  const isJustificationMissing = requiresJustification && !review.reviewJustification.trim();

  const updateReview = (key, value) => {
    setReview(currentReview => {
      const nextReview = clearResolvedIssues({ ...currentReview, [key]: value }, key, value);
      const nextFinalDecision = RESULT_REVIEW_KEYS.includes(key)
        ? decisionFromResultValues(
          nextReview.validityIsValid,
          nextReview.conformityIsConformant,
          nextReview.priceIsConformant,
        )
        : decisionFromResults(
          nextReview.finalDecision,
          nextReview.validityIsValid,
          nextReview.conformityIsConformant,
          nextReview.priceIsConformant,
        );

      return {
        ...nextReview,
        finalDecision: nextFinalDecision,
      };
    });
  };

  const saveReview = () => {
    if (!onReviewSave) return null;
    if (isJustificationMissing) return Promise.reject();

    return onReviewSave(buildPayload(review));
  };

  const handleConfirm = () => {
    const savePromise = saveReview();
    const confirm = () => (onConfirm ? onConfirm() : null);

    if (!savePromise) {
      const confirmPromise = confirm();
      if (!confirmPromise || !confirmPromise.then) {
        onClose();
        return confirmPromise;
      }

      return confirmPromise.then(onClose).catch(() => null);
    }

    return savePromise
      .then(confirm)
      .then(onClose)
      .catch(() => null);
  };

  const handleSendModificationRequest = () => {
    const message = buildModificationRequestMessage(review);

    const savePromise = saveReview();
    if (savePromise) {
      savePromise.then(() => {
        onSendModificationRequest(message);
        onClose();
      });
    } else {
      onSendModificationRequest(message);
      onClose();
    }
  };

  return (
    <Box mt={4} p={4} bg="white" rounded="md" border="1px solid" borderColor="lightGray3">
      <Stack spacing={3}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Text fontSize="xs" color="gray.500" textTransform="uppercase" fontWeight="bold" letterSpacing="0.05em">
            Analyse intelligente
          </Text>
          <Box
            {...decisionStyle(finalDecision)}
            rounded="full"
            fontWeight="bold"
            fontSize="xs"
            px={3}
            py={1}
            textTransform="uppercase"
            letterSpacing="0.04em"
          >
            {decisionLabel(finalDecision)}
          </Box>
        </Box>

        <Box bg="lightGray4" rounded="md" px={3} py={3}>
          <Stack spacing={2}>
            <SummaryLine
              label="Validité administrative"
              tone={validitySummaryTone}
              value={validitySummaryText}
            />
            <SummaryLine
              label="Préconisations"
              tone={conformitySummaryTone}
              value={conformitySummaryText}
            />
            <SummaryLine
              label="Prix"
              tone={priceSummaryTone}
              value={priceSummary(priceSummaryValue, priceFlags)}
            />
          </Stack>
        </Box>

        {isAutoFollowupDisabled && !isActionHidden && (
          <Text fontSize="sm" color="semiDarkGray.600">
            Suivi automatique désactivé : le comparateur n&apos;envoie aucun mail et ne valide pas le devis.
          </Text>
        )}

        {!isAutoFollowupDisabled && analysis.autoFollowupSkippedAt && !isActionHidden && (
          <Text fontSize="sm" color="semiDarkGray.600">
            Analysé pendant que le suivi automatique était désactivé : le comparateur n&apos;a ni validé le
            devis ni envoyé de mail. À traiter à la main.
          </Text>
        )}

        {analysis.operatorReviewRequestedAt && !analysis.reviewedAt && !analysis.appliedAt && !isActionHidden && (
          <Text fontSize="sm" color="coral.700" data-testid="operator-review-notice">
            Contrôle opérateur demandé le {formatDateTime(analysis.operatorReviewRequestedAt)}
            {analysis.operatorReviewReason ? ` : ${analysis.operatorReviewReason}` : ''}. Le comparateur n&apos;a ni
            validé le devis ni envoyé de demande de modification.
          </Text>
        )}

        <Box display="flex" justifyContent="flex-end" alignItems="center">
          {!isActionHidden && (
            <SecondaryButton size="sm" onClick={onLaunchOrRetry} disabled={analysis.loading} mr={3}>
              Relancer l&apos;analyse
            </SecondaryButton>
          )}
          <SecondaryButton size="sm" onClick={onOpen}>
            Voir l&apos;analyse intelligente
          </SecondaryButton>
        </Box>
      </Stack>

      <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent
          maxW="1040px"
          rounded="lg"
          my={4}
          height="calc(100vh - 32px)"
          maxH="calc(100vh - 32px)"
          display="flex"
          flexDirection="column"
        >
          <ModalHeader p={0} borderBottom="1px solid" borderColor="lightGray3">
            <Box px={6} py={4} display="flex" alignItems="center" justifyContent="space-between" pr="60px">
              <Box>
                <Text fontSize="lg" fontWeight="bold" color="darkGray" lineHeight="short">
                  Analyse intelligente du devis
                </Text>
                <Text fontSize="xs" color="gray.500" mt={1} fontWeight="normal">
                  Vérifiez et ajustez si nécessaire avant de confirmer.
                </Text>
              </Box>
              <Box
                {...decisionStyle(finalDecision)}
                rounded="full"
                fontWeight="bold"
                fontSize="xs"
                px={3}
                py={1}
                textTransform="uppercase"
                letterSpacing="0.04em"
              >
                {decisionLabel(finalDecision)}
              </Box>
            </Box>
          </ModalHeader>
          <ModalCloseButton _focus={{ boxShadow: 'none' }} />
          <ModalBody bg="#F6F7F8" pt={5} pb="96px" px={6} flex="1 1 auto" minH={0} overflowY="auto">
            <AnalysisReviewFields
              review={review}
              updateReview={updateReview}
              isReadOnly={isReadOnly}
              requiresJustification={requiresJustification}
            />
          </ModalBody>
          <ModalFooter
            borderTop="1px solid"
            borderColor="lightGray3"
            bg="white"
            px={6}
            py={3}
            flex="0 0 auto"
            position="sticky"
            bottom={0}
            zIndex={1}
            boxShadow="0 -2px 6px rgba(35, 40, 45, 0.04)"
          >
            {isConfirmed && (
              <Text fontSize="md" color="green.600" fontWeight="bold">
                Analyse intelligente confirmée
              </Text>
            )}

            {!isConfirmed && isFinalized && (
              <Text fontSize="md" color="gray.600" fontWeight="bold">
                Analyse intelligente finalisée
              </Text>
            )}

            {!isActionHidden && (
              <Box width="100%" display="flex" alignItems="center" justifyContent="space-between">
                <SecondaryButton onClick={saveReview} disabled={analysis.loading || isJustificationMissing}>
                  Enregistrer la revue
                </SecondaryButton>

                <Box display="flex" alignItems="center">
                  {!isConformant && !isNonConformant && (
                    <Text fontSize="sm" color="orange.700" fontWeight="bold" mr={4}>
                      Vérification manuelle requise
                    </Text>
                  )}

                  {isConformant && (
                    <>
                      <SecondaryButton
                        onClick={handleSendModificationRequest}
                        disabled={analysis.loading || isJustificationMissing}
                        mr={3}
                      >
                        Demande de modifications
                      </SecondaryButton>
                      <PrimaryButton onClick={handleConfirm} disabled={analysis.loading || isJustificationMissing}>
                        Confirmer l&apos;analyse intelligente
                      </PrimaryButton>
                    </>
                  )}

                  {isNonConformant && (
                    <PrimaryButton onClick={handleSendModificationRequest} disabled={analysis.loading || isJustificationMissing}>
                      Demande de modifications
                    </PrimaryButton>
                  )}
                </Box>
              </Box>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default AiAnalysisPanel;
